import { ensureGPTServicesEnabled, whenReady, getAdProvider } from '@/helpers/ad/ad-helper';
import { WebLocale } from '@/enums/web-locale';

/**
 * Configuration for a native ad slot
 */
export interface NativeSlotConfig {
	/** Slot ID - also used as HTML element ID where the ad will render */
	slotId: string;
	/** GAM ad unit path (e.g., '/21679119891/native_sqtv_list_buybox') */
	adUnitPath: string;
	/** Targeting key-value pairs for ad delivery */
	targeting: Record<string, string>;
	/** Ad slot sizes - defaults to ['fluid', [1, 1]] for SQTV, use 'fluid' for backdrop */
	sizes?: 'fluid' | ['fluid', [1, 1]] | any;
}

/**
 * Native ad loading status
 */
export type AdStatus = 'loading' | 'rendered' | 'failed' | 'destroyed';

export interface GamResponseInfo {
	advertiserId: number;
	campaignId: number;
	creativeId: number;
	lineItemId: number;
}

/**
 * Native ad key-value targeting identifiers
 */
export type NativeAdKeyValueTargeting =
	| 'native_backdrop'
	| 'native_sqtv_list'
	| 'native_sqtv_dynamic'
	| 'native_list_offer';

/**
 * Configuration for loading a native ad slot with lifecycle management
 */
export interface LoadNativeSlotConfig {
	/** Web locale for consent management */
	webLocale: WebLocale;
	/** Slot ID - also used as HTML element ID where the ad will render */
	slotId: string;
	/** GAM ad unit path (e.g., '/21679119891/native_sqtv_list_buybox') */
	adUnitPath: string;
	/** Key-value targeting identifier - always set to 'true' */
	keyValueTargeting: NativeAdKeyValueTargeting;
	/** Additional targeting key-value pairs (optional) */
	targeting?: Record<string, string>;
	/** Ad slot sizes - defaults to ['fluid', [1, 1]] for SQTV, use 'fluid' for backdrop */
	sizes?: 'fluid' | ['fluid', [1, 1]] | any;
	/** Timeout in milliseconds (default: 3000) */
	timeout?: number;
	/** Callback when ad successfully renders */
	onRendered: () => void;
	/** Callback when ad fails to load or render */
	onFailed: () => void;
	/** Callback with GAM response metadata (advertiserId, campaignId, creativeId, lineItemId) */
	onResponseInfo?: (info: GamResponseInfo) => void;
}

/**
 * Defines a new native ad slot or refreshes an existing one with updated targeting.
 * This function checks if a slot with the given slotId already exists:
 * - If exists: Clears old targeting, applies new targeting, and refreshes the slot
 * - If not exists: Defines new slot, applies targeting, displays, and refreshes
 *
 * ```
 */
export function defineOrRefreshNativeSlot(config: NativeSlotConfig): void {
	window.googletag.cmd.push(() => {
		// Check if slot already exists
		const slots = window.googletag.pubads().getSlots();
		const existingSlot = slots.find(slot => slot.getSlotElementId() === config.slotId);

		if (existingSlot) {
			// Refresh existing slot with new targeting
			existingSlot.clearTargeting();
			Object.entries(config.targeting).forEach(([key, value]) => {
				existingSlot.setTargeting(key, value);
			});
			window.googletag.pubads().refresh([existingSlot]);
		} else {
			// Define new slot
			const sizes = config.sizes || ['fluid', [1, 1]];
			const slot = window.googletag.defineSlot(config.adUnitPath, sizes, config.slotId);

			if (!slot) {
				console.error('[Native Ad] Failed to define slot:', config.slotId);
				return;
			}

			// Apply targeting
			Object.entries(config.targeting).forEach(([key, value]) => {
				slot.setTargeting(key, value);
			});

			slot.addService(window.googletag.pubads());
			ensureGPTServicesEnabled();
			window.googletag.display(config.slotId);
			window.googletag.pubads().refresh([slot]);
		}
	});
}

/**
 * Destroys a native ad slot and clears its content.
 * This should be called when a component unmounts to prevent memory leaks
 * and ensure clean state for subsequent ad loads.

 */
export function destroyNativeSlot(slotId: string): void {
	// Ensure googletag exists before destroying
	if (!window.googletag || !window.googletag.cmd) return;

	window.googletag.cmd.push(() => {
		// Find the slot object by its element ID
		const slots = window.googletag.pubads().getSlots();
		const slotToRemove = slots.find(slot => slot.getSlotElementId() === slotId);

		if (slotToRemove) {
			window.googletag.pubads().clear([slotToRemove]);
			window.googletag.destroySlots([slotToRemove]);

			// Clear the div content to fully reset
			const divElement = document.getElementById(slotId);
			if (divElement) {
				divElement.innerHTML = '';
			}
		}
	});
}

/**
 * Waits for Google Publisher Tag (GPT) to be available using existing ad infrastructure
 * Native ads require GPT which is loaded by both Yieldlove and GAM
 */
async function waitForGoogleTag(webLocale: WebLocale): Promise<boolean> {
	try {
		// Detect which ad provider this locale uses (Yieldlove or GAM)
		const adProvider = getAdProvider(webLocale, false);
		await whenReady(webLocale, adProvider);

		// Verify pubads is available
		if (window.googletag.pubads!) {
			return true;
		}

		console.error('[Native Ad] GPT pubads not available after provider ready');
		return false;
	} catch (err) {
		console.error('[Native Ad] Failed to wait for ad provider:', err);
		return false;
	}
}

/**
 * Loads a native ad slot with full lifecycle management including:
 * - Consent waiting (GDPR/privacy)
 * - Ad blocker detection
 * - Slot definition
 * - Event listeners for render success/failure
 * - Timeout handling
 *
 */
export async function loadNativeSlot(config: LoadNativeSlotConfig): Promise<void> {
	let hasResolved = false;

	// Wait for GPT to be available (includes consent waiting)
	const gptAvailable = await waitForGoogleTag(config.webLocale);
	if (!gptAvailable) {
		config.onFailed();
		return;
	}

	// Merge key-value targeting with additional targeting
	const fullTargeting = {
		[config.keyValueTargeting]: 'true',
		...config.targeting,
	};

	// Define the native ad slot
	defineOrRefreshNativeSlot({
		slotId: config.slotId,
		adUnitPath: config.adUnitPath,
		targeting: fullTargeting,
		sizes: config.sizes,
	});

	// Set up timeout for ad load failure
	const loadTimeout = setTimeout(() => {
		if (!hasResolved) {
			hasResolved = true;
			config.onFailed();
		}
	}, config.timeout || 3000);

	// Listen for ad render event
	window.googletag.cmd.push(() => {
		window.googletag.pubads().addEventListener('slotRenderEnded', (event: any) => {
			if (event.slot.getSlotElementId() === config.slotId && !hasResolved) {
				hasResolved = true;
				clearTimeout(loadTimeout);

				if (!event.isEmpty) {
					const response = event?.slot?.getResponseInformation();
					if (response && config.onResponseInfo) {
						config.onResponseInfo({
							advertiserId: response.advertiserId,
							campaignId: response.campaignId,
							creativeId: response.creativeId,
							lineItemId: response.lineItemId,
						});
					}
					config.onRendered();
				} else {
					config.onFailed();
				}
			}
		});
	});
}
